import {
  Children,
  cloneElement,
  isValidElement,
  type PropsWithChildren,
  type ReactElement,
} from "react";
import { DataTable } from "primereact/datatable";
import type { DataTableFilterMetaData } from "primereact/datatable";
import type { ColumnProps } from "primereact/column";

import {
  DEFAULT_PAGE,
  ROWS_PER_PAGE,
  GRID_PAGES,
} from "../../../utils/constants/table";
import HighlightText from "../HighlightText";

type ColumnBodyFnT = (data: unknown, options: unknown) => React.ReactNode;

const wrapBodyWithHighlight = (
  child: ReactElement<ColumnProps>,
  searchText: string,
) => {
  const { field, body: originalBody } = child.props;

  const wrappedBody = (rowData) => {
    const result =
      typeof originalBody === "function"
        ? (originalBody as ColumnBodyFnT)(rowData, {})
        : rowData[field];

    if (typeof result === "string" || typeof result === "number") {
      return <HighlightText text={result} highlight={searchText} />;
    }

    return result;
  };

  return cloneElement(child, { body: wrappedBody });
};

type DataTableSortMetaT = NonNullable<
  React.ComponentProps<typeof DataTable>["multiSortMeta"]
>;

type ColaboraDynamicTablePropsT = React.ComponentProps<typeof DataTable> & PropsWithChildren<{
  data?: Record<string, unknown>[];
  isLoadingData?: boolean;
  numberOfRowsPerPage?: number;
  firstPage?: number;
  sort: DataTableSortMetaT;
  onChangeSort: (multiSortMeta: DataTableSortMetaT) => void;
  useHighlightSearching?: boolean;
}>;

const ColaboraDynamicTable = ({
  data,
  isLoadingData,
  numberOfRowsPerPage = ROWS_PER_PAGE,
  sort,
  onChangeSort,
  children,
  useHighlightSearching = true,
  firstPage = DEFAULT_PAGE,
  ...props
}: ColaboraDynamicTablePropsT) => {
  const rowsPerPage = numberOfRowsPerPage ?? ROWS_PER_PAGE;

  const handleOnChangeSort = (e) => {
    onChangeSort(e.multiSortMeta);
  };

  const globalFilter = props.filters?.global as
    | DataTableFilterMetaData
    | undefined;

  const searchText = useHighlightSearching
    ? (globalFilter?.value?.trim() ?? "")
    : "";

  const resolvedChildren = searchText
    ? Children.map(children, (child) => {
        if (
          !isValidElement<ColumnProps>(child) ||
          (child.type as { displayName?: string })?.displayName !== "Column"
        )
          return child;
        return wrapBodyWithHighlight(child, searchText);
      })
    : children;

  return (
    <DataTable
      value={data}
      loading={isLoadingData}
      first={firstPage}
      rows={rowsPerPage}
      scrollHeight="flex"
      scrollDirection="both"
      size="small"
      className="flex w-full h-full relative !font-karla !text-[14px]"
      tableClassName="!absolute !inset-0"
      paginatorClassName="!border-t !border-border !justify-start !text-xs"
      paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown "
      rowsPerPageOptions={GRID_PAGES}
      currentPageReportTemplate="Total de registros {totalRecords}"
      onSort={handleOnChangeSort}
      multiSortMeta={sort}
      sortMode="multiple"
      scrollable
      paginator
      {...props}
    >
      {resolvedChildren}
    </DataTable>
  );
};

export default ColaboraDynamicTable;
